import React from 'react'
import { ApolloConsumer } from '@apollo/react-common'
import { Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Formik, FormikProps } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import clsx from 'clsx'

import { AffiliateAccountDocument } from '@src/queries/AffiliateAccount.generated'
import { ConfirmAffiliationDocument } from '@src/mutations/ConfirmAffiliationMutation.generated'

import { OriginalError, extractData } from '@utils/errorHandler'
import messages from '@utils/messages'
import { orderCenterRoutes, useRouter, support } from '@src/routes'
import { titleCase } from '@src/styles/utils/textManipulation'
import vrStyles from '@src/styles/utils/vrStyles'
import { Linq } from '@src/components/Linq'
import {
  SnackbarMessages,
  SnackbarType,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import CalendarMonthIcon from '@icons/CalendarMonthIcon'
import AccountAffiliationForm from './AccountAffiliationForm'
import { sendCommonDetailClickEvent } from '@src/utils/analytics'

const useStyles = makeStyles((theme) => {
  const { vr2 } = vrStyles

  return {
    notFound: {
      border: `1px solid ${theme.palette.grey[500]}`,
      borderLeft: 0,
      borderRight: 0,
      display: 'flex',
      padding: theme.spacing(8, 4),
      marginTop: theme.spacing(8),
    },
    notFoundIcon: {
      color: theme.palette.grey[500],
      height: 55,
      width: 55,
      marginRight: theme.spacing(4),
    },
    vr2,
    titleCase,
  }
})
// Form Values Types
export interface LinkProfileFormValues {
  accountInfo: string
  customerNumberValue: string
  transactionalInfo: string
  transactionalInfoValue: string
}

export enum AffiliationType {
  Orders = 'orders',
  Quotes = 'quotes',
}

const transactionalInfoValues = {
  [AffiliationType.Orders]: 'ordernumber',
  [AffiliationType.Quotes]: 'quotereferencenumber',
}

const searchPageUrls = {
  [AffiliationType.Orders]: orderCenterRoutes.orderSearch(),
  [AffiliationType.Quotes]: orderCenterRoutes.quoteSearch(),
}

interface AccountAffiliationProps {
  affiliationType: AffiliationType
  hasItems: boolean
}

const AccountAffiliation = ({
  affiliationType,
  hasItems,
}: AccountAffiliationProps) => {
  const classes = useStyles()
  const router = useRouter()
  const intl = useIntl()

  const { setSnackbar, setGlobalSnackbarState } = useGlobalSnackbar()

  return (
    <ApolloConsumer>
      {(client) => (
        <Formik
          initialValues={{
            accountInfo: 'customernumber',
            transactionalInfo: transactionalInfoValues[affiliationType],
            customerNumberValue: '',
            transactionalInfoValue: '',
          }}
          validateOnChange={false}
          validateOnBlur={false}
          validate={(values) => {
            const errorMessages: string[] = []

            const readableFieldNames = {
              customernumber: intl.formatMessage(messages.CUSTOMER_NUMBER),
              ordernumber: intl.formatMessage(messages.PREVIOUS_NUMBER),
              invoicenumber: intl.formatMessage({
                id: 'INVOICE_NUMBER',
                defaultMessage: 'Invoice Number',
              }),
              packingslipnumber: intl.formatMessage(
                messages.PACKING_SLIP_NUMBER
              ),
              quotereferencenumber: intl.formatMessage(
                messages.QUOTE_REFERENCE
              ),
            }

            if (
              !values.customerNumberValue.trim() ||
              !values.transactionalInfoValue.trim()
            ) {
              if (!values.customerNumberValue.trim()) {
                errorMessages.push(
                  intl.formatMessage(messages.FIELD_REQUIRED_NAME, {
                    fieldName: readableFieldNames[`${values.accountInfo}`],
                  })
                )
              }

              if (!values.transactionalInfoValue.trim()) {
                errorMessages.push(
                  intl.formatMessage(messages.FIELD_REQUIRED_NAME, {
                    fieldName:
                      readableFieldNames[`${values.transactionalInfo}`],
                  })
                )
              }

              const errorMessage = [
                intl.formatMessage(messages.FIELD_REQUIRED_COUNT, {
                  numErrors: errorMessages.length,
                }),
                ...errorMessages,
              ].join(' , ')

              setGlobalSnackbarState({
                open: true,
                message: errorMessage,
                variant: SnackbarType.Error,
              })
            }
          }}
          onSubmit={async (values, formikBag) => {
            formikBag.setSubmitting(true)
            try {
              const { data } = await client.query({
                query: AffiliateAccountDocument,

                variables: {
                  [values.accountInfo]: values.customerNumberValue,
                  [values.transactionalInfo]: values.transactionalInfoValue,
                },
              })
              const partnerData = data.affiliateAccount
              await client.mutate({
                mutation: ConfirmAffiliationDocument,

                variables: {
                  data: {
                    billTo:
                      partnerData.billToAddress &&
                      partnerData.billToAddress.partnerNumber,
                    soldTo:
                      partnerData.soldToAddress &&
                      partnerData.soldToAddress.partnerNumber,
                    shipTo:
                      partnerData.shipToAddress &&
                      partnerData.shipToAddress.partnerNumber,
                    payer:
                      partnerData.payerAddress &&
                      partnerData.payerAddress.partnerNumber,
                    salesOrg:
                      partnerData.soldToAddress &&
                      partnerData.soldToAddress.salesOrg,
                  },
                },
              })
              const href =
                router?.query?.returnUrl ?? searchPageUrls[affiliationType]

              const detailMap = {
                [AffiliationType.Orders]: 'order number',
                [AffiliationType.Quotes]: 'quote reference',
              }
              const detail = detailMap[affiliationType] ?? ''
              sendCommonDetailClickEvent({
                event: 'profile_interaction',
                action: 'self affiliation',
                detail,
                section: 'account',
                component: 'body',
                elementType: 'button',
                elementText: 'connect',
                coreEvent: 'no',
              })

              window.location.href = href
            } catch (error) {
              const { errors, hasError } = extractData(error as OriginalError)
              if (
                hasError(
                  'ERROR_NO_RECORD_FOUND_FOR_GIVEN_COMBINATION_OF_CUSTOMER_DETAILS'
                )
              ) {
                setSnackbar(
                  SnackbarMessages.ErrorNoRecordFoundForGivenCustomerDetails
                )
              } else if (
                hasError('CUSTOMER_NOT_ALLOWED_TO_LINK_PROFILE_BEFORE')
              ) {
                if (errors.length > 0) {
                  const { misc } = errors[0]
                  if (
                    misc &&
                    misc.ecommerceErrors &&
                    misc.ecommerceErrors.length > 0 &&
                    misc.ecommerceErrors[0].paramList &&
                    misc.ecommerceErrors[0].paramList.length > 1
                  ) {
                    const [mins, secs] = misc.ecommerceErrors[0].paramList
                    setSnackbar(
                      SnackbarMessages.CustomerNotAllowedToLinkProfileBefore,
                      [mins, secs]
                    )
                  } else {
                    setSnackbar(
                      SnackbarMessages.CustomerNotAllowedToLinkProfileBefore,
                      [15, 0]
                    )
                  }
                } else {
                  setSnackbar(
                    SnackbarMessages.CustomerNotAllowedToLinkProfileBefore,
                    [15, 0]
                  )
                }
              } else {
                setSnackbar(SnackbarMessages.GenericError)
              }
            }
            formikBag.setSubmitting(false)
          }}
        >
          {(formikBag: FormikProps<LinkProfileFormValues>) => {
            return (
              <>
                <AccountAffiliationForm
                  affiliationType={affiliationType}
                  formikBag={formikBag}
                ></AccountAffiliationForm>

                {!hasItems && (
                  <div className={classes.notFound}>
                    <CalendarMonthIcon className={classes.notFoundIcon} />
                    <div>
                      <Typography
                        component="h2"
                        variant="h3"
                        className={clsx(classes.vr2, classes.titleCase)}
                      >
                        <FormattedMessage
                          id="ACCOUNT_AFFILIATION_NOT_FOUND"
                          defaultMessage="No {type} Found"
                          values={{
                            type: affiliationType,
                          }}
                        ></FormattedMessage>
                      </Typography>

                      <Typography component="p">
                        <FormattedMessage
                          id="ACCOUNT_AFFILIATION_CONTACT_INFO"
                          defaultMessage="If you have any questions, please contact {link}."
                          values={{
                            link: (
                              <Linq
                                href={support.customerSupport()}
                                target="_blank"
                              >
                                <FormattedMessage
                                  id="ACCOUNT_AFFILIATION_CONTACT_INFO_LINK"
                                  defaultMessage="customer support"
                                />
                              </Linq>
                            ),
                            type: affiliationType,
                          }}
                        ></FormattedMessage>
                      </Typography>
                    </div>
                  </div>
                )}
              </>
            )
          }}
        </Formik>
      )}
    </ApolloConsumer>
  )
}

export default AccountAffiliation
